import { createSelector, OutputSelector } from 'reselect';

import { RootModel } from '../models';
import { FacilityTypes, ProjectItemTypes } from '../../types';
import { NONE_GROUP_FACILITY_IN_AMENITY } from '../../commons/constants';
import { isValidAmenity } from '../../utils/logicUtils';

export const getFacilities = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  { [key: string]: FacilityTypes },
  string,
  string,
  string,
  FacilityTypes[]
>(
  (state) => state.projectItemEntity.ids,
  (state) => state.facilityEntity.ids,
  (state) => state.amenitiesModel.projectItemId,
  (state) => state.amenitiesModel.groupFacilityKey,
  (state) => state.layoutModel.isMobileDevice,
  (projectItemEntityIds, facilityEntityIds, projectItemId, groupFacilityKey, isMobileDevice) => {
    const amenity = projectItemEntityIds[projectItemId];
    const groupFacility = facilityEntityIds[groupFacilityKey];
    if (!amenity || !amenity.facilityCodes) return null;
    const facilities = [];

    if (groupFacilityKey && groupFacilityKey !== NONE_GROUP_FACILITY_IN_AMENITY.key) {
      // get facilities in group facility
      if (groupFacility && groupFacility.facilityCodes && groupFacility.facilityCodes.length) {
        groupFacility.facilityCodes.forEach((item) => {
          if (facilityEntityIds && facilityEntityIds[item]) {
            facilities.push(facilityEntityIds[item]);
          }
        });
      }
    } else if (
      // get facilities none group in amenity
      groupFacilityKey &&
      isMobileDevice &&
      groupFacilityKey === NONE_GROUP_FACILITY_IN_AMENITY.key
    ) {
      amenity.facilityCodes.forEach((item) => {
        const facility = facilityEntityIds[item];
        if (facility && facility.numberOfFacilities === 0) {
          facilities.push(facility);
        }
      });
    } else {
      // get facilities in amenity
      amenity.facilityCodes.forEach((item) => {
        if (facilityEntityIds[item]) {
          facilities.push(facilityEntityIds[item]);
        }
      });
    }
    // sort by orderIndex
    return facilities.sort((a, b) => a.orderIndex - b.orderIndex);
  }
);

export const getSelectedFacility = createSelector<
  RootModel,
  { [key: string]: FacilityTypes },
  string,
  FacilityTypes
>(
  (state) => state.facilityEntity.ids,
  (state) => state.amenitiesModel.facilityKey,
  (facilityEntityIds, facilityKey) => {
    return facilityEntityIds[facilityKey];
  }
);

export const getGroupFacility = createSelector<
  RootModel,
  { [key: string]: FacilityTypes },
  string,
  FacilityTypes
>(
  (state) => state.facilityEntity.ids,
  (state) => state.amenitiesModel.groupFacilityKey,
  (facilityEntityIds, facilityKey) => facilityEntityIds[facilityKey]
);

export const getFocusFacility = createSelector<
  RootModel,
  { [key: string]: FacilityTypes },
  string,
  FacilityTypes
>(
  (state) => state.facilityEntity.ids,
  (state) => state.amenitiesModel.focusFacilityKey,
  (facilityEntityIds, focusFacilityKey) => {
    return facilityEntityIds[focusFacilityKey];
  }
);

export const getAmenityBreadcrumb = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  any // TODO: need define return type
>(
  (state) => state.projectItemEntity.ids,
  (projectItemEntityIds) => {
    const amenities = Object.values(projectItemEntityIds).filter((amenity) => {
      return isValidAmenity(amenity);
    });

    return amenities
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((amenity) => {
        const { name, code, id, groupFacilities, facilities } = amenity;
        const groupsFacility = [];
        const facilitiesNoneGroup = [];

        const totalFacilities = [...facilities, ...groupFacilities];
        if (totalFacilities && totalFacilities.length) {
          totalFacilities.forEach((facility) => {
            if (facility.numberOfFacilities > 0) {
              groupsFacility.push({ name: facility.name, code: facility.code });
            } else {
              facilitiesNoneGroup.push({ name: facility.name, code: facility.code });
            }
          });
        }

        if (facilitiesNoneGroup.length && groupsFacility.length) {
          groupsFacility.push({
            name: NONE_GROUP_FACILITY_IN_AMENITY.label,
            code: NONE_GROUP_FACILITY_IN_AMENITY.key,
          });
        }
        return { id, name, code, groupsFacility };
      });
  }
);

export const getFacilitiesByKey = (
  groupCodes: string[] = [],
  itemCodes: string[] = []
): OutputSelector<
  RootModel,
  FacilityTypes[],
  // eslint-disable-next-line function-paren-newline
  (facilityEntities: { [key: string]: FacilityTypes }) => FacilityTypes[]
> =>
  createSelector<RootModel, { [key: string]: FacilityTypes }, FacilityTypes[]>(
    (state) => state.facilityEntity.ids,
    (facilityEntities) => {
      const items: FacilityTypes[] = [];
      itemCodes.forEach((code) => {
        const item = facilityEntities[code];
        if (item) items.push(item);
      });

      groupCodes.forEach((code) => {
        const item = facilityEntities[code];
        if (item) items.push(item);
      });
      // sort by orderIndex
      return items.sort((a, b) => a.orderIndex - b.orderIndex);
    }
  );

export const getFacilityOverviewAmenity = createSelector<
  RootModel,
  { [key: string]: FacilityTypes },
  string,
  string,
  FacilityTypes
>(
  (state) => state.facilityEntity.ids,
  (state) => state.amenitiesOverviewModel.facilityKey,
  (state) => state.amenitiesOverviewModel.groupFacilityKey,
  (facilityEntityIds, facilityKey, groupFacilityKey) => {
    return facilityEntityIds[groupFacilityKey || facilityKey];
  }
);

export const getFacilitiesByAmenityKeyOverview = createSelector<
  RootModel,
  { [key: string]: FacilityTypes },
  { [key: string]: ProjectItemTypes },
  string,
  FacilityTypes[]
>(
  (state) => state.facilityEntity.ids,
  (state) => state.projectItemEntity.ids,
  (state) => state.amenitiesOverviewModel.amenityKey,
  (facilityEntityIds, projectItemEntity, amenityKey) => {
    const facilities: FacilityTypes[] = [];
    const amenity = Object.values(projectItemEntity).find((item) => item.code === amenityKey);

    if (!amenity) return;
    const { facilityCodes, groupFacilityCodes } = amenity;

    (facilityCodes || []).forEach((item) => {
      const facility = facilityEntityIds[item];
      if (facility) facilities.push(facility);
    });

    (groupFacilityCodes || []).forEach((item) => {
      const facility = facilityEntityIds[item];
      if (facility) facilities.push(facility);
    });
    // sort by orderIndex
    return facilities.sort((a, b) => a.orderIndex - b.orderIndex);
  }
);
